/**
 * GamificationContext - Phase 4
 * 
 * Centralized state management for gamification features.
 * Provides streak, XP, level, and badge data to all components.
 * 
 * @module GamificationContext
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUserStats,
  processTaskCompletion,
  awardXP,
  calculateLevel,
  calculateLevelProgress,
  BADGES,
  LEVELS,
} from '../services/gamificationService';

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const GamificationContext = createContext(null);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const GamificationProvider = ({ children }) => {
  const { user } = useAuth();
  
  // State
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Celebration state
  const [celebration, setCelebration] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  /**
   * Load user's gamification stats
   */
  const loadStats = useCallback(async () => {
    if (!user?.uid) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userStats = await getUserStats(user.uid);
      setStats(userStats);
      setError(null);
    } catch (err) {
      console.error('[GamificationContext] Error loading stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Load stats on mount and user change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const level = stats ? calculateLevel(stats.totalXP || 0) : LEVELS[0];
  const levelProgress = stats ? calculateLevelProgress(stats.totalXP || 0) : { current: 0, required: 100, percentage: 0 };
  
  const earnedBadges = stats?.earnedBadges?.map(id => {
    const badge = Object.values(BADGES).find(b => b.id === id);
    return badge || null;
  }).filter(Boolean) || [];

  const lockedBadges = Object.values(BADGES).filter(
    badge => !stats?.earnedBadges?.includes(badge.id)
  );

  // ============================================================================
  // CELEBRATION HANDLERS
  // ============================================================================

  /**
   * Trigger a celebration animation
   */
  const triggerCelebration = useCallback((type, data) => {
    setCelebration({ type, data, timestamp: Date.now() });
    setShowCelebration(true);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      setShowCelebration(false);
    }, 4000);
  }, []);

  /**
   * Dismiss celebration manually
   */
  const dismissCelebration = useCallback(() => {
    setShowCelebration(false);
  }, []);

  // ============================================================================
  // TASK COMPLETION HANDLER
  // ============================================================================

  /**
   * Process task completion and trigger rewards
   */
  const onTaskComplete = useCallback(async (task) => {
    if (!user?.uid) return null;

    try {
      const result = await processTaskCompletion(user.uid, task);
      
      if (result) {
        // Update local stats
        setStats(prev => ({
          ...prev,
          ...result.stats,
          totalXP: result.xpResult.totalXP,
        }));

        // Trigger celebrations
        if (result.xpResult.leveledUp) {
          triggerCelebration('levelUp', {
            oldLevel: result.xpResult.oldLevel,
            newLevel: result.xpResult.newLevel,
          });
        } else if (result.xpResult.newBadges?.length > 0) {
          triggerCelebration('badge', {
            badges: result.xpResult.newBadges,
          });
        } else if (result.streakUpdate.currentStreak % 7 === 0 && result.streakUpdate.currentStreak > 0) {
          triggerCelebration('streakMilestone', {
            streak: result.streakUpdate.currentStreak,
          });
        }

        return result;
      }
    } catch (err) {
      console.error('[GamificationContext] Error processing task completion:', err);
    }

    return null;
  }, [user?.uid, triggerCelebration]);

  // ============================================================================
  // XP AWARD HANDLER
  // ============================================================================

  /**
   * Award XP for various actions
   */
  const onAwardXP = useCallback(async (action, context = {}) => {
    if (!user?.uid) return null;

    try {
      const result = await awardXP(user.uid, action, context);
      
      if (result) {
        // Update local stats
        setStats(prev => ({
          ...prev,
          totalXP: result.totalXP,
        }));

        // Trigger level up celebration
        if (result.leveledUp) {
          triggerCelebration('levelUp', {
            oldLevel: result.oldLevel,
            newLevel: result.newLevel,
          });
        }

        return result;
      }
    } catch (err) {
      console.error('[GamificationContext] Error awarding XP:', err);
    }

    return null;
  }, [user?.uid, triggerCelebration]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = {
    // Stats
    stats,
    loading,
    error,
    
    // Computed values
    level,
    levelProgress,
    currentStreak: stats?.currentStreak || 0,
    longestStreak: stats?.longestStreak || 0,
    totalXP: stats?.totalXP || 0,
    tasksCompletedTotal: stats?.tasksCompletedTotal || 0,
    tasksCompletedToday: stats?.tasksCompletedToday || 0,
    
    // Badges
    earnedBadges,
    lockedBadges,
    allBadges: BADGES,
    
    // Levels
    allLevels: LEVELS,
    
    // Actions
    loadStats,
    onTaskComplete,
    onAwardXP,
    
    // Celebration
    celebration,
    showCelebration,
    triggerCelebration,
    dismissCelebration,
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export default GamificationContext;
